import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import FormEmbed from "@/components/FormEmbed";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeQuoteForm(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/65 backdrop-blur-sm p-3 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
            role="dialog"
            aria-modal="true"
            aria-label="Get your free estimate"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              /* Never taller than the viewport — the body scrolls, the header stays put */
              className="relative w-full max-w-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)] flex flex-col widget-shell"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header — close button sits inside the panel so it can't be clipped */}
              <div className="flex items-start justify-between gap-4 bg-card px-5 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0 border-b border-border">
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl text-foreground font-bold tracking-tight">
                    Get Your Free Estimate
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">
                    Tell us about the project — we'll get back to you.
                  </p>
                </div>
                <button
                  onClick={closeQuoteForm}
                  className="shrink-0 h-9 w-9 rounded-full bg-muted text-foreground/70 hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                  aria-label="Close form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto overscroll-contain bg-card">
                <FormEmbed id="popup-estimate-form" className="p-4 sm:p-6" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
